import { Link } from "react-router-dom";
import { ChefHat, Home as HomeIcon, HeartHandshake, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const services = [
  {
    icon: ChefHat,
    title: "Cooking",
    tagline: "Good Food. Happy Family.",
    description: "Experienced helpers with the culinary skills to prepare meals your family will love. From Filipino comfort dishes to Asian, Western and confinement cuisine, we match cooks whose taste and technique suit your family's palate.",
    highlights: ["Meal planning support", "Dietary restrictions handled", "Confinement food specialists available"],
  },
  {
    icon: HomeIcon,
    title: "Housekeeping",
    tagline: "A Clean Home. A Clear Mind.",
    description: "Detail-oriented helpers who keep your home immaculate, organised and cared for every day. From daily upkeep to seasonal deep cleans, our helpers respect your home like their own.",
    highlights: ["Wardrobe & laundry care", "Deep cleaning routines", "Comfortable with premium finishes"],
  },
  {
    icon: HeartHandshake,
    title: "Special Needs Care",
    tagline: "Care Beyond Routine.",
    description: "Experience, patience, emotional maturity and compassion to support families with special needs. We rigorously screen for the specific temperament and training required to provide meaningful, sustained care.",
    highlights: ["Autism & ADHD experience", "Dementia care available", "Behavioural therapy-trained helpers"],
  },
];

export default function Services() {
  const { t } = useLang();
  return (
    <div data-testid="services-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <div className="overline mb-6">{t("services.overline")}</div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#0A1128] max-w-4xl">
          {t("services.title1")} <span className="italic text-[#688A4E]">{t("services.title2")}</span> {t("services.title3")}
        </h1>
      </section>

      <section className="w-full h-[420px] sm:h-[520px] overflow-hidden" data-testid="services-hero-image">
        <img
          src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/r353n6m2_Helper_maroonshirt_kitchen.jpeg"
          alt="Happy Home helper preparing a meal in the kitchen"
          className="w-full h-full object-cover object-top"
        />
      </section>

      <section className="border-y border-gray-200">
        {services.map((s, i) => (
          <div key={s.title} className={`border-b border-gray-200 last:border-0 ${i % 2 === 1 ? "bg-[#F9F9F7]" : ""}`}>
            {i === 1 && (
              <div className="w-full h-[380px] sm:h-[460px] overflow-hidden border-b border-gray-200" data-testid="services-housekeeping-image">
                <img src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/1yoqkael_Helper_Duo.jpeg" alt="Happy Home housekeeping team" className="w-full h-full object-cover object-top"/>
              </div>
            )}
            {i === 2 && (
              <div className="w-full h-[380px] sm:h-[460px] overflow-hidden border-b border-gray-200" data-testid="services-specialneeds-image">
                <img src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/793yn23u_WhatsApp%20Image%202026-07-04%20at%202.12.53%20AM.jpeg" alt="Happy Home special needs and elderly care" className="w-full h-full object-cover object-center"/>
              </div>
            )}
            <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24 grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-2">
                <div className="text-xs tracking-[0.28em] text-gray-400 mb-6">0{i+1} · Service</div>
                <div className="w-14 h-14 border border-[#0A1128] flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-[#0A1128]"/>
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="overline mb-3">{s.tagline}</div>
                <h2 className="font-serif text-4xl sm:text-5xl text-[#0A1128] mb-6">{s.title}</h2>
                <p className="text-lg text-gray-700 leading-relaxed">{s.description}</p>
              </div>
              <div className="lg:col-span-4">
                <div className="border-l border-[#688A4E] pl-6">
                  <div className="overline mb-4">Highlights</div>
                  <ul className="space-y-3 text-gray-700">
                    {s.highlights.map((h) => (
                      <li key={h} className="flex gap-3 items-start">
                        <span className="text-[#688A4E] mt-1">✦</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-4">The Happy Home Process</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[#0A1128] mb-6">Four steps to your right match.</h2>
            <p className="text-gray-700 leading-relaxed mb-8">Every recommendation begins with understanding your family. From consultation to placement — and for a full year after — we're with you.</p>
            <Link to="/application" className="btn-primary" data-testid="services-start-application">Start Your Application <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <ol className="space-y-8">
            {[
              { n: "01", t: "Consultation", d: "We understand your family, routines and priorities." },
              { n: "02", t: "Precision Match™", d: "We shortlist helpers scored against your specific criteria." },
              { n: "03", t: "Meet & Choose", d: "You interview candidates with our guidance every step." },
              { n: "04", t: "365-Day Care™", d: "We continue supporting you well beyond placement day." },
            ].map((step) => (
              <li key={step.n} className="grid grid-cols-[80px_1fr] gap-6 border-b border-gray-200 pb-6">
                <div className="font-serif text-4xl text-[#688A4E]">{step.n}</div>
                <div>
                  <div className="font-serif text-2xl text-[#0A1128] mb-1">{step.t}</div>
                  <div className="text-gray-600">{step.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}
